import {
    LayoutDashboard,
    Briefcase,
    ClipboardList,
    Activity,
    Users,
    Settings,
    Building2,
    Network,
    CreditCard,
    Package,
    type LucideIcon
} from 'lucide-react';
import { type UserRole } from '@/types/team';

export type NavContext = 'global' | 'office' | 'department';

export interface NavItem {
    id: string;
    label: string;
    icon: LucideIcon;
    to: string; // Dynamic paths will be replaced at runtime (e.g., :officeId)
    rolesAllowed: UserRole[];
    context: NavContext[]; // Where this item appears
    requiresScope?: 'my' | 'department' | 'office' | 'org'; // Query param scope hint
    featureFlag?: string;
    section?: 'primary' | 'organize' | 'measure' | 'configure'; // For visual grouping
    exact?: boolean; // If true, only matches exactly, rather than also matching prefix routes
}

export const navConfig: NavItem[] = [
    // --- GLOBAL CONTEXT (Enterprise) ---
    {
        id: 'global-dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/',
        rolesAllowed: ['OWNER', 'ORG_ADMIN'], // Restricted: Lower roles use Dept/Office items
        context: ['global'],
        section: 'primary'
    },
    {
        id: 'global-kanban',
        label: 'Operations Board',
        icon: ClipboardList,
        to: '/kanban',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'FINANCE'],
        context: ['global'],
        section: 'primary'
    },
    {
        id: 'global-jobs',
        label: 'Jobs / Claims',
        icon: Briefcase,
        to: '/jobs',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'FINANCE'],
        context: ['global'],
        section: 'primary'
    },

    // Organize (Global)
    {
        id: 'global-offices',
        label: 'Offices',
        icon: Building2,
        to: '/offices',
        rolesAllowed: ['OWNER', 'ORG_ADMIN'],
        context: ['global'],
        section: 'organize'
    },
    {
        id: 'global-equipment-catalog',
        label: 'Equipment',
        icon: Package,
        to: '/equipment/catalog',
        rolesAllowed: ['OWNER', 'ORG_ADMIN'],
        context: ['global'],
        section: 'organize'
    },
    {
        id: 'global-departments',
        label: 'Departments',
        icon: Network,
        to: '/departments',
        rolesAllowed: ['OWNER', 'ORG_ADMIN'],
        context: ['global'],
        section: 'organize'
    },
    {
        id: 'global-users',
        label: 'Team',
        icon: Users,
        to: '/users',
        rolesAllowed: ['OWNER', 'ORG_ADMIN'],
        context: ['global'],
        section: 'organize'
    },

    // Reports (Global)
    {
        id: 'global-reports-ops',
        label: 'Operational Reports',
        icon: Activity,
        to: '/reports/ops',
        rolesAllowed: ['OWNER', 'ORG_ADMIN'],
        context: ['global'],
        section: 'measure'
    },
    // Configure (Global)
    {
        id: 'global-settings',
        label: 'Org Settings',
        icon: Settings,
        to: '/settings/org',
        rolesAllowed: ['OWNER', 'ORG_ADMIN'],
        context: ['global'],
        section: 'configure'
    },
    {
        id: 'global-billing',
        label: 'Billing & Plan',
        icon: CreditCard,
        to: '/billing',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'FINANCE'],
        context: ['global'],
        section: 'configure'
    },

    // --- OFFICE CONTEXT ---
    {
        id: 'office-dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/office/:officeId/dashboard',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN'],
        context: ['office'],
        section: 'primary'
    },
    {
        id: 'office-kanban',
        label: 'Operations Board',
        icon: ClipboardList,
        to: '/office/:officeId/kanban',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN'],
        context: ['office'],
        section: 'primary'
    },
    {
        id: 'office-jobs',
        label: 'Jobs / Claims',
        icon: Briefcase,
        to: '/office/:officeId/jobs',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN'],
        context: ['office'],
        section: 'primary'
    },
    {
        id: 'office-departments',
        label: 'Departments',
        icon: Network,
        to: '/office/:officeId/depts',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN'],
        context: ['office'],
        section: 'organize'
    },
    {
        id: 'office-equipment',
        label: 'Equipment',
        icon: Package,
        to: '/office/:officeId/equipment',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN'],
        context: ['office'],
        section: 'organize'
    },
    {
        id: 'office-team',
        label: 'Team',
        icon: Users,
        to: '/office/:officeId/team',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN'],
        context: ['office'],
        section: 'organize'
    },
    {
        id: 'office-settings',
        label: 'Office Settings',
        icon: Settings,
        to: '/office/:officeId/settings',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN'],
        context: ['office'],
        section: 'configure'
    },

    // --- DEPARTMENT CONTEXT (Unified) ---
    {
        id: 'dept-dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/office/:officeId/department/:departmentId',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER'],
        context: ['department'],
        section: 'primary',
        exact: true
    },
    {
        id: 'dept-kanban',
        label: 'Operations Board',
        icon: ClipboardList,
        to: '/office/:officeId/department/:departmentId/kanban',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER'],
        context: ['department'],
        section: 'primary'
    },
    {
        id: 'dept-jobs',
        label: 'Jobs / Claims',
        icon: Briefcase,
        to: '/office/:officeId/department/:departmentId/jobs',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER'],
        context: ['department'],
        section: 'primary'
    },
    {
        id: 'dept-team',
        label: 'Team',
        icon: Users,
        to: '/office/:officeId/department/:departmentId/team',
        rolesAllowed: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER'],
        context: ['department'],
        section: 'organize'
    }
];
